import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const Register = () => {
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: 'admin' // Default role
  });
  const [formError, setFormError] = useState('');
  const navigate = useNavigate();
  const { register, error, clearError, isAuthenticated, loading } = useAuth();

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  // Clear auth context error when component unmounts
  useEffect(() => {
    return () => clearError();
  }, [clearError]);

  // Actualizar el estado de error local cuando cambia el error del contexto de autenticación
  useEffect(() => {
    if (error) {
      // Traducir mensajes de error comunes del inglés al español
      if (error === 'User already exists') {
        setFormError('El usuario ya existe');
      } else if (error === 'Registration failed') {
        setFormError('Error en el registro');
      } else {
        setFormError(error);
      }
    }
  }, [error]);

  const { full_name, email, password, confirmPassword, role } = formData;

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    // Validar formulario
    if (!full_name || !email || !password || !confirmPassword) {
      setFormError('Por favor complete todos los campos');
      return;
    }

    if (password !== confirmPassword) {
      setFormError('Las contraseñas no coinciden');
      return;
    }

    // Validación de contraseña robusta
    if (password.length < 8 || password.length > 17) {
      setFormError('La contraseña debe tener entre 8 y 17 caracteres');
      return;
    }
    
    // Validar que contenga al menos un número
    if (!/\d/.test(password)) {
      setFormError('La contraseña debe contener al menos un número');
      return;
    }
    
    // Validar que contenga al menos una minúscula
    if (!/[a-z]/.test(password)) {
      setFormError('La contraseña debe contener al menos una letra minúscula');
      return;
    }
    
    // Validar que contenga al menos una mayúscula
    if (!/[A-Z]/.test(password)) {
      setFormError('La contraseña debe contener al menos una letra mayúscula');
      return;
    }

    // Register user
    const userData = { full_name, email, password, role };
    const result = await register(userData);
    
    if (result.success && result.needsVerification) {
      // Redirect to verification page
      navigate('/verify-otp', { state: { email } });
    } else if (result.success) {
      navigate('/dashboard');
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-form-container">
        <div className="auth-header">
          <h2>Crear una Cuenta</h2>
          <p>Registro gratuito para accesar a Ventaz Web</p>
        </div>
        
        {formError && (
          <div className="error-message">
            <i className="fas fa-exclamation-circle"></i> {formError}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="full_name">Nombre Completo</label>
            <div className="input-with-icon">
              <i className="fas fa-user input-icon"></i>
              <input
                id="full_name"
                type="text"
                name="full_name"
                value={full_name}
                onChange={handleChange}
                disabled={loading}
                placeholder="Ingrese su nombre completo"
                required
                onInvalid={(e) => e.target.setCustomValidity('Por favor ingrese su nombre completo')}
                onInput={(e) => e.target.setCustomValidity('')}
              />
            </div>
          </div>
          
          <div className="form-group">
            <label htmlFor="email">Correo Electrónico</label>
            <div className="input-with-icon">
              <i className="fas fa-envelope input-icon"></i>
              <input
                id="email"
                type="email"
                name="email"
                value={email}
                onChange={handleChange}
                disabled={loading}
                placeholder="Ingrese su correo electrónico"
                required
                onInvalid={(e) => e.target.setCustomValidity('Por favor ingrese un correo electrónico válido')}
                onInput={(e) => e.target.setCustomValidity('')}
                autoComplete="username"
              />
            </div>
          </div>
          
          <div className="form-group">
            <label htmlFor="password">Contraseña</label>
            <div className="input-with-icon">
              <i className="fas fa-lock input-icon"></i>
              <input
                id="password"
                type="password"
                name="password"
                value={password}
                onChange={handleChange}
                disabled={loading}
                placeholder="Ingrese su contraseña"
                required
                onInvalid={(e) => e.target.setCustomValidity('Por favor ingrese una contraseña')}
                onInput={(e) => e.target.setCustomValidity('')}
                autoComplete="new-password"
              />
            </div>
            <div className="password-requirements">
              <small>
                La contraseña debe tener:
                <ul>
                  <li>Entre 8 y 17 caracteres</li>
                  <li>Al menos un número</li>
                  <li>Al menos una letra minúscula</li>
                  <li>Al menos una letra mayúscula</li>
                </ul>
              </small>
            </div>
          </div>
          
          <div className="form-group">
            <label htmlFor="confirmPassword">Confirmar Contraseña</label>
            <div className="input-with-icon">
              <i className="fas fa-lock input-icon"></i>
              <input
                id="confirmPassword"
                type="password"
                name="confirmPassword"
                value={confirmPassword}
                onChange={handleChange}
                disabled={loading}
                placeholder="Confirme su contraseña"
                required
                onInvalid={(e) => e.target.setCustomValidity('Por favor confirme su contraseña')}
                onInput={(e) => e.target.setCustomValidity('')}
                autoComplete="new-password"
              />
            </div>
          </div>
          
          <button 
            type="submit" 
            className="btn-primary" 
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="spinner"></span> Registrando...
              </>
            ) : (
              'Crear Cuenta'
            )}
          </button>
        </form>
        
        <div className="auth-footer">
          <p>
            ¿Ya tiene una cuenta? <Link to="/login">Iniciar Sesión</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
